import { useRef, useCallback } from 'react';
import { PlatformService } from '@lightning/services';

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

interface UseCarouselTouchOptions {
  onTap?: () => void;
  threshold?: number;
  tapDuration?: number;
}

export function useCarouselTouch({
  onTap,
  threshold = 10, // pixels of movement allowed for a tap
  tapDuration = 200, // max milliseconds for a tap
}: UseCarouselTouchOptions = {}) {
  const touchStartRef = useRef<TouchPosition | null>(null);
  const isTouchMovingRef = useRef(false);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    // Only handle touch events on native platforms
    if (!PlatformService.isNative() && e.type.includes('touch')) {
      return;
    }

    const point = 'touches' in e ? e.touches[0] : e;
    touchStartRef.current = {
      x: point.clientX,
      y: point.clientY,
      time: Date.now(),
    };
    isTouchMovingRef.current = false;

    // Clear any existing timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
      tapTimeoutRef.current = null;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!touchStartRef.current) return;

    const point = 'touches' in e ? e.touches[0] : e;
    const deltaX = Math.abs(point.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(point.clientY - touchStartRef.current.y);

    // If movement exceeds threshold, it's a swipe
    if (deltaX > threshold || deltaY > threshold) {
      isTouchMovingRef.current = true;
    }
  }, [threshold]);

  const handleTouchEnd = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!touchStartRef.current) return;

    const duration = Date.now() - touchStartRef.current.time;

    // It's a tap if: no significant movement AND duration is short
    if (!isTouchMovingRef.current && duration < tapDuration && onTap) {
      // Use a small timeout to allow carousel to process the event first
      tapTimeoutRef.current = setTimeout(() => {
        onTap();
      }, 50);
    }

    // Reset
    touchStartRef.current = null;
    isTouchMovingRef.current = false;
  }, [tapDuration, onTap]);

  const handlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onMouseDown: handleTouchStart,
    onMouseMove: handleTouchMove,
    onMouseUp: handleTouchEnd,
  };

  // Cleanup function
  const cleanup = useCallback(() => {
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
      tapTimeoutRef.current = null;
    }
  }, []);

  return { handlers, cleanup };
}
